/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Check if a supplier is within the service radius of an RFQ
 * @param {Object} rfqLocation - RFQ location object
 * @param {Object} supplierLocation - Supplier location object
 * @param {number} targetRadius - Target radius for RFQ (km)
 * @returns {boolean} True if supplier is within range
 */
export const isSupplierInRange = (rfqLocation, supplierLocation, targetRadius) => {
  if (!rfqLocation?.latitude || !supplierLocation?.latitude) {
    return false;
  }

  const distance = calculateDistance(
    rfqLocation.latitude,
    rfqLocation.longitude,
    supplierLocation.latitude,
    supplierLocation.longitude
  );

  // Check if supplier is within RFQ's target radius AND supplier's service radius
  const supplierServiceRadius = supplierLocation.serviceRadius || 50;
  return distance <= targetRadius && distance <= supplierServiceRadius;
};

/**
 * Find suppliers within a given radius of an RFQ
 * @param {Object} rfqLocation - RFQ location object
 * @param {Array} suppliers - Array of supplier objects
 * @param {number} targetRadius - Target radius for RFQ (km)
 * @returns {Array} Array of suppliers within range
 */
export const findSuppliersInRange = (rfqLocation, suppliers, targetRadius) => {
  if (!rfqLocation?.latitude) {
    return [];
  }

  return suppliers.filter(supplier => {
    if (!supplier.locationData?.latitude) {
      return false;
    }

    return isSupplierInRange(rfqLocation, supplier.locationData, targetRadius);
  });
};

/**
 * Calculate service areas based on location and radius
 * @param {number} latitude - Latitude of the location
 * @param {number} longitude - Longitude of the location
 * @param {number} radius - Service radius in kilometers
 * @returns {Array} Array of nearby states/cities
 */
export const calculateServiceAreas = (latitude, longitude, radius) => {
  const nigerianCities = [
    { name: "Lagos", lat: 6.5244, lng: 3.3792, state: "Lagos" },
    { name: "Abuja", lat: 9.0820, lng: 7.3986, state: "FCT" },
    { name: "Kano", lat: 11.9914, lng: 8.5317, state: "Kano" },
    { name: "Ibadan", lat: 7.3961, lng: 3.8969, state: "Oyo" },
    { name: "Kaduna", lat: 10.5222, lng: 7.4384, state: "Kaduna" },
    { name: "Port Harcourt", lat: 4.8156, lng: 7.0498, state: "Rivers" },
    { name: "Benin City", lat: 6.3176, lng: 5.6145, state: "Edo" },
    { name: "Maiduguri", lat: 11.8333, lng: 13.1500, state: "Borno" },
    { name: "Zaria", lat: 11.0667, lng: 7.7000, state: "Kaduna" },
    { name: "Aba", lat: 5.1167, lng: 7.3667, state: "Abia" },
    { name: "Jos", lat: 9.8965, lng: 8.8583, state: "Plateau" },
    { name: "Ilorin", lat: 8.5000, lng: 4.5500, state: "Kwara" },
    { name: "Oyo", lat: 7.8500, lng: 3.9333, state: "Oyo" },
    { name: "Enugu", lat: 6.4400, lng: 7.5000, state: "Enugu" },
    { name: "Abeokuta", lat: 7.1500, lng: 3.3500, state: "Ogun" },
    { name: "Calabar", lat: 4.9757, lng: 8.3417, state: "Cross River" },
    { name: "Uyo", lat: 5.0333, lng: 7.9167, state: "Akwa Ibom" },
    { name: "Owerri", lat: 5.4833, lng: 7.0333, state: "Imo" },
    { name: "Lokoja", lat: 7.8000, lng: 6.7333, state: "Kogi" },
    { name: "Warri", lat: 5.5167, lng: 5.7500, state: "Delta" }
  ];

  const nearbyAreas = [];
  
  nigerianCities.forEach(city => {
    const distance = calculateDistance(latitude, longitude, city.lat, city.lng);
    if (distance <= radius) {
      nearbyAreas.push(city.state);
    }
  });
  
  return [...new Set(nearbyAreas)]; // Remove duplicates
};

/**
 * Format location for display
 * @param {Object} location - Location object
 * @returns {string} Formatted location string
 */
export const formatLocation = (location) => {
  if (!location) return 'Location not specified';
  
  // Handle new Google Places API structure
  if (location.name) {
    return location.name;
  }
  
  // Handle old structure with coordinates
  if (location.coordinates) {
    return `${location.coordinates.latitude.toFixed(4)}, ${location.coordinates.longitude.toFixed(4)}`;
  }
  
  // Handle string locations
  if (typeof location === 'string') {
    return location;
  }
  
  return 'Location not specified';
};

/**
 * Get location from coordinates (reverse geocoding)
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<Object>} Location object with name and state
 */
export const getLocationFromCoordinates = async (latitude, longitude) => {
  // In a real implementation, you would use a geocoding service
  // For now, we'll use a simple lookup against Nigerian cities
  const nigerianCities = [
    { name: "Lagos", lat: 6.5244, lng: 3.3792, state: "Lagos" },
    { name: "Abuja", lat: 9.0820, lng: 7.3986, state: "FCT" },
    { name: "Kano", lat: 11.9914, lng: 8.5317, state: "Kano" },
    { name: "Ibadan", lat: 7.3961, lng: 3.8969, state: "Oyo" },
    { name: "Kaduna", lat: 10.5222, lng: 7.4384, state: "Kaduna" },
    { name: "Port Harcourt", lat: 4.8156, lng: 7.0498, state: "Rivers" },
    { name: "Benin City", lat: 6.3176, lng: 5.6145, state: "Edo" },
    { name: "Maiduguri", lat: 11.8333, lng: 13.1500, state: "Borno" },
    { name: "Zaria", lat: 11.0667, lng: 7.7000, state: "Kaduna" },
    { name: "Aba", lat: 5.1167, lng: 7.3667, state: "Abia" },
    { name: "Jos", lat: 9.8965, lng: 8.8583, state: "Plateau" },
    { name: "Ilorin", lat: 8.5000, lng: 4.5500, state: "Kwara" },
    { name: "Oyo", lat: 7.8500, lng: 3.9333, state: "Oyo" },
    { name: "Enugu", lat: 6.4400, lng: 7.5000, state: "Enugu" },
    { name: "Abeokuta", lat: 7.1500, lng: 3.3500, state: "Ogun" },
    { name: "Calabar", lat: 4.9757, lng: 8.3417, state: "Cross River" },
    { name: "Uyo", lat: 5.0333, lng: 7.9167, state: "Akwa Ibom" },
    { name: "Owerri", lat: 5.4833, lng: 7.0333, state: "Imo" },
    { name: "Lokoja", lat: 7.8000, lng: 6.7333, state: "Kogi" },
    { name: "Warri", lat: 5.5167, lng: 5.7500, state: "Delta" }
  ];

  // Find the closest city
  let closestCity = null;
  let minDistance = Infinity;

  nigerianCities.forEach(city => {
    const distance = calculateDistance(latitude, longitude, city.lat, city.lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestCity = city;
    }
  });

  if (closestCity && minDistance <= 50) { // Within 50km of a major city
    return {
      name: closestCity.name,
      state: closestCity.state,
      coordinates: {
        latitude: latitude,
        longitude: longitude
      }
    };
  }

  // If not near a major city, return coordinates
  return {
    name: 'Custom Location',
    state: 'Nigeria',
    coordinates: {
      latitude: latitude,
      longitude: longitude
    }
  };
};

/**
 * Validate location object
 * @param {Object} location - Location object to validate
 * @returns {boolean} True if location is valid
 */
export const validateLocation = (location) => {
  if (!location) return false;
  
  // Check for new Google Places API structure
  if (location.name && location.latitude && location.longitude) {
    return true;
  }
  
  // Check for old structure
  if (location.coordinates && location.coordinates.latitude && location.coordinates.longitude) {
    return true;
  }
  
  return false;
};

/**
 * Get current user location using browser geolocation
 * @returns {Promise<Object>} Location object
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
}; 